import { useState, useEffect, useMemo } from 'react';
import {
  Search,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Wrench,
  Bot,
  FileText,
  Puzzle,
  Server,
  Filter,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getAssetsGrouped } from '@/services/assetService';
import type { AssetSummary, AssetType, AssetGroupedResponse } from '@/types';
import { cn } from '@/lib/utils';

// Asset type configuration for display
const ASSET_TYPE_CONFIG: Record<AssetType, {
  label: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  description: string;
}> = {
  infrastructure: {
    label: '인프라',
    icon: Server,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    description: '데이터베이스, 스토리지 등 인프라 컴포넌트',
  },
  tool: {
    label: '도구',
    icon: Wrench,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: '웹 검색, 코드 실행 등 도구',
  },
  agent: {
    label: '에이전트',
    icon: Bot,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    description: 'AI 에이전트 컴포넌트',
  },
  template: {
    label: '템플릿',
    icon: FileText,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: '문서 및 코드 템플릿',
  },
  rag_module: {
    label: 'RAG 모듈',
    icon: Puzzle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    description: 'RAG 파이프라인 모듈',
  },
};

// Node type mapping for workflow
const ASSET_TO_NODE_TYPE: Record<AssetType, string> = {
  infrastructure: 'process',
  tool: 'task',
  agent: 'task',
  template: 'process',
  rag_module: 'process',
};

interface DraggableAssetItemProps {
  asset: AssetSummary;
  onDragStart: (event: React.DragEvent, asset: AssetSummary) => void;
}

function DraggableAssetItem({ asset, onDragStart }: DraggableAssetItemProps) {
  const typeConfig = ASSET_TYPE_CONFIG[asset.assetType];
  const IconComponent = typeConfig.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            draggable
            onDragStart={(e) => onDragStart(e, asset)}
            className={cn(
              "flex items-center gap-2 p-2 rounded-md border cursor-grab",
              "hover:bg-accent hover:border-primary/50 transition-colors",
              "active:cursor-grabbing"
            )}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className={cn("p-1.5 rounded", typeConfig.bgColor)}>
              <IconComponent className={cn("h-4 w-4", typeConfig.color)} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium truncate">{asset.name}</span>
                {asset.isBuiltin && (
                  <Badge variant="secondary" className="text-[10px] px-1 py-0">
                    System
                  </Badge>
                )}
              </div>
              {asset.category && (
                <span className="text-xs text-muted-foreground truncate block">
                  {asset.category}
                </span>
              )}
            </div>
            {asset.mcpCompatible && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 flex-shrink-0">
                MCP
              </Badge>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[250px]">
          <div className="space-y-1">
            <p className="font-medium">{asset.name}</p>
            {asset.description && (
              <p className="text-xs text-muted-foreground">{asset.description}</p>
            )}
            <div className="flex items-center gap-2 pt-1">
              <Badge variant="secondary" className="text-[10px]">
                {typeConfig.label}
              </Badge>
              {asset.category && (
                <Badge variant="outline" className="text-[10px]">
                  {asset.category}
                </Badge>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              드래그하여 워크플로우에 추가
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface AssetGroupSectionProps {
  type: AssetType;
  assets: AssetSummary[];
  onDragStart: (event: React.DragEvent, asset: AssetSummary) => void;
  defaultOpen?: boolean;
}

function AssetGroupSection({ type, assets, onDragStart, defaultOpen = false }: AssetGroupSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const typeConfig = ASSET_TYPE_CONFIG[type];
  const IconComponent = typeConfig.icon;

  if (assets.length === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full p-2 hover:bg-accent rounded-md transition-colors">
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <div className={cn("p-1 rounded", typeConfig.bgColor)}>
          <IconComponent className={cn("h-4 w-4", typeConfig.color)} />
        </div>
        <span className="text-sm font-medium">{typeConfig.label}</span>
        <Badge variant="secondary" className="ml-auto text-[10px]">
          {assets.length}
        </Badge>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-6 pr-2 space-y-1 mt-1">
          {assets.map((asset) => (
            <DraggableAssetItem
              key={asset.id}
              asset={asset}
              onDragStart={onDragStart}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export interface AssetDropData {
  asset: AssetSummary;
  nodeType: string;
  nodeData: {
    label: string;
    type: string;
    status: 'pending';
    description?: string;
    assetId: string;
    assetType: AssetType;
    assetSlug: string;
    mcpCompatible: boolean;
  };
}

interface AssetPaletteProps {
  projectId?: string;
  onAssetDrop?: (data: AssetDropData, position: { x: number; y: number }) => void;
  className?: string;
  filterTypes?: AssetType[];
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function AssetPalette({
  projectId,
  className,
  filterTypes,
  collapsed = false,
}: AssetPaletteProps) {
  const [assets, setAssets] = useState<AssetGroupedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<AssetType | 'all'>('all');

  useEffect(() => {
    async function fetchAssets() {
      setLoading(true);
      try {
        const data = await getAssetsGrouped(projectId);
        setAssets(data);
      } catch (error) {
        console.error('Failed to fetch assets:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchAssets();
  }, [projectId]);

  // Filter assets based on search query and selected type
  const filteredAssets = useMemo(() => {
    if (!assets) return {};

    const result: Partial<Record<AssetType, AssetSummary[]>> = {};
    const typeOrder: AssetType[] = ['tool', 'agent', 'template', 'rag_module', 'infrastructure'];

    for (const type of typeOrder) {
      // Skip types not in filter list if filter is specified
      if (filterTypes && !filterTypes.includes(type)) continue;
      if (selectedType !== 'all' && type !== selectedType) continue;

      const assetList = assets.assets[type] || [];

      const filtered = assetList.filter((asset) => {
        if (!searchQuery) return true;

        const query = searchQuery.toLowerCase();
        return (
          asset.name.toLowerCase().includes(query) ||
          asset.description?.toLowerCase().includes(query) ||
          asset.category?.toLowerCase().includes(query) ||
          asset.tags?.some((tag) => tag.toLowerCase().includes(query))
        );
      });

      if (filtered.length > 0) {
        result[type] = filtered;
      }
    }

    return result;
  }, [assets, searchQuery, selectedType, filterTypes]);

  const totalAssetCount = useMemo(() => {
    return Object.values(filteredAssets).reduce((sum, list) => sum + list.length, 0);
  }, [filteredAssets]);

  const handleDragStart = (event: React.DragEvent, asset: AssetSummary) => {
    const dropData: AssetDropData = {
      asset,
      nodeType: ASSET_TO_NODE_TYPE[asset.assetType],
      nodeData: {
        label: asset.name,
        type: ASSET_TO_NODE_TYPE[asset.assetType],
        status: 'pending',
        description: asset.description,
        assetId: asset.id,
        assetType: asset.assetType,
        assetSlug: asset.slug,
        mcpCompatible: asset.mcpCompatible,
      },
    };

    event.dataTransfer.setData('application/reactflow', JSON.stringify(dropData));
    event.dataTransfer.effectAllowed = 'move';
  };

  if (collapsed) {
    return (
      <div className={cn("w-12 border-r bg-background flex flex-col items-center py-2 gap-2", className)}>
        <TooltipProvider>
          {Object.entries(ASSET_TYPE_CONFIG).map(([type, config]) => {
            if (filterTypes && !filterTypes.includes(type as AssetType)) return null;
            const IconComponent = config.icon;
            const count = assets?.assets[type as AssetType]?.length || 0;

            return (
              <Tooltip key={type}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn("relative", config.color)}
                  >
                    <IconComponent className="h-5 w-5" />
                    {count > 0 && (
                      <span className="absolute -top-1 -right-1 text-[10px] bg-primary text-primary-foreground rounded-full px-1 min-w-[16px] text-center">
                        {count}
                      </span>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>{config.label} ({count})</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={cn("w-64 border-r bg-background p-3 space-y-3", className)}>
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-8 w-full" />
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("w-64 border-r bg-background flex flex-col", className)}>
      {/* Header */}
      <div className="p-3 border-b space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">에셋 팔레트</h3>
          <Badge variant="secondary" className="text-[10px]">
            {totalAssetCount}개
          </Badge>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="에셋 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {/* Type Filter */}
        <Select
          value={selectedType}
          onValueChange={(v) => setSelectedType(v as AssetType | 'all')}
        >
          <SelectTrigger className="h-8 text-sm">
            <Filter className="h-3.5 w-3.5 mr-2" />
            <SelectValue placeholder="유형 선택" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">모든 유형</SelectItem>
            {Object.entries(ASSET_TYPE_CONFIG).map(([type, config]) => {
              if (filterTypes && !filterTypes.includes(type as AssetType)) return null;
              const IconComponent = config.icon;
              return (
                <SelectItem key={type} value={type}>
                  <div className="flex items-center gap-2">
                    <IconComponent className={cn("h-4 w-4", config.color)} />
                    {config.label}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Asset List */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {Object.keys(filteredAssets).length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              {searchQuery ? '검색 결과가 없습니다.' : '사용 가능한 에셋이 없습니다.'}
            </div>
          ) : (
            Object.entries(filteredAssets).map(([type, assetList]) => (
              <AssetGroupSection
                key={type}
                type={type as AssetType}
                assets={assetList}
                onDragStart={handleDragStart}
                defaultOpen={selectedType !== 'all' || Object.keys(filteredAssets).length === 1}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer hint */}
      <div className="p-2 border-t">
        <p className="text-[10px] text-muted-foreground text-center">
          에셋을 드래그하여 워크플로우 캔버스에 추가하세요
        </p>
      </div>
    </div>
  );
}

export default AssetPalette;
